// Social media post generator — turns an event into a share-ready image card +
// pre-written caption (GH #77). This module holds the pure, unit-tested content
// builders (spec + caption + filename). Canvas drawing lives in socialCardRender.ts
// so the heavier react-dom/server code only ships in the lazy modal chunk.

import type { EventOccurrence, FellowshipEvent } from '@fellowship/shared'
import { getEventCta } from '@fellowship/shared'

export type PostFormat = 'feed' | 'story'

export interface PostFormatMeta {
  key: PostFormat
  label: string
  ratio: string
  width: number
  height: number
}

// 1:1 feed (Instagram/Facebook) and 9:16 stories. 1080px base — the standard
// social upload width; downscales cleanly and stays under a few hundred KB as PNG.
export const POST_FORMATS: Record<PostFormat, PostFormatMeta> = {
  feed: { key: 'feed', label: 'Feed', ratio: '1:1', width: 1080, height: 1080 },
  story: { key: 'story', label: 'Story', ratio: '9:16', width: 1080, height: 1920 },
}

export interface SocialCardSpec {
  eventType: string
  title: string
  dateLabel: string // "Saturday, July 12"
  timeLabel: string // "6:00 PM"
  locationLabel: string // city / venue shown on the card
  hostName: string
  hostLogoUrl: string | null // church logo or host avatar
  heroImageUrl: string | null
  url: string // full event URL (used in caption)
  siteLabel: string // bare domain shown on the card footer, e.g. "fellowship-app.com"
}

export interface BuildPostArgs {
  event: FellowshipEvent
  occurrence?: EventOccurrence | null
  hostName: string
  hostLogoUrl?: string | null
  heroImageUrl?: string | null
  /** City for the caption / fallback location label. */
  city?: string | null
  /** Denomination, used to seed a hashtag. */
  denomination?: string | null
  /** window.location.origin */
  origin: string
}

export interface PostContent {
  spec: SocialCardSpec
  caption: string
}

function formatDateLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })
}

function formatTimeLabel(iso: string): string {
  return new Date(iso)
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .replace(':00', '') // "6:00 PM" → "6 PM" for a cleaner card
}

/** First sentence of a description, trimmed to a tweet-ish length. */
function firstSentence(text: string | null | undefined, max = 180): string {
  if (!text) return ''
  const cleaned = text.replace(/\s+/g, ' ').trim()
  if (!cleaned) return ''
  const match = cleaned.match(/^.*?[.!?](\s|$)/)
  const sentence = (match ? match[0] : cleaned).trim()
  if (sentence.length <= max) return sentence
  return `${sentence.slice(0, max - 1).trimEnd()}…`
}

/** Turn an arbitrary phrase into a CamelCase hashtag token (no leading #). */
function hashtagToken(value: string): string {
  return value
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('')
}

const TYPE_HASHTAG: Record<string, string> = {
  potluck: 'Potluck',
  worship: 'Worship',
  study: 'BibleStudy',
  social: 'Fellowship',
  outdoors: 'Outdoors',
  groups: 'SmallGroup',
  kids: 'Youth',
  service: 'Serve',
}

/**
 * Build the card spec + a ready-to-post caption from an event. Pure: no DOM, no
 * canvas — safe to unit test and to call during render.
 */
export function buildPostContent(args: BuildPostArgs): PostContent {
  const { event, occurrence, origin } = args
  const url = `${origin}/events/${event.id}`
  const siteLabel = origin.replace(/^https?:\/\//, '').replace(/\/$/, '')

  const dateLabel = occurrence ? formatDateLabel(occurrence.startsAt) : ''
  const timeLabel = occurrence ? formatTimeLabel(occurrence.startsAt) : ''
  const locationLabel = event.locationName?.trim() || args.city?.trim() || 'See link for details'

  const spec: SocialCardSpec = {
    eventType: event.eventType ?? 'potluck',
    title: event.title,
    dateLabel,
    timeLabel,
    locationLabel,
    hostName: args.hostName,
    hostLogoUrl: args.hostLogoUrl ?? null,
    heroImageUrl: args.heroImageUrl ?? null,
    url,
    siteLabel,
  }

  // ── Caption ──────────────────────────────────────────────────────────────
  const when = [dateLabel, timeLabel].filter(Boolean).join(' · ')
  const cta = getEventCta(event.eventType ?? 'potluck')
  const blurb = firstSentence(event.description)

  const hashtags = [
    '#Fellowship',
    '#ChurchEvents',
    `#${TYPE_HASHTAG[event.eventType ?? 'potluck'] ?? 'Fellowship'}`,
    args.city ? `#${hashtagToken(args.city)}` : null,
    args.denomination ? `#${hashtagToken(args.denomination)}` : null,
  ].filter(Boolean)

  const lines = [
    event.title,
    '',
    when ? `🗓️ ${when}` : null,
    `📍 ${locationLabel}`,
    blurb ? '' : null,
    blurb || null,
    '',
    `${cta} 👉 ${url}`,
    '',
    hashtags.join(' '),
  ].filter((l) => l !== null)

  return { spec, caption: (lines as string[]).join('\n') }
}

/** Download filename for the rendered card, e.g. "summer-potluck-feed.png". */
export function formatPostFilename(title: string, format: PostFormat): string {
  const slug =
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50) || 'fellowship-event'
  return `${slug}-${format}.png`
}
